import { useCallback, useEffect, useState } from "react";
import { Badge, Button, Card } from "../components/ui";
import { api, type OptimizerConfigProfile } from "../data/api";
import {
  DEFAULT_OPTIMIZER_CONFIG,
  type OptimizerConfig,
} from "../features/programacion/optimizerConfig";

// AdminOptimizerConfigsPage — gestor CRUD de perfiles del optimizador.
// Cada perfil agrupa todos los settings (jornada, % peso/volumen, proxies,
// stability_penalty) bajo un nombre. El planner selecciona un perfil al
// iniciar planificación; no más editar números inline cada vez.
//
// Reglas operativas:
//   - Siempre debe existir al menos un perfil (el backend rechaza borrar
//     el último).
//   - Exactamente un perfil es "default": el que sale auto-seleccionado
//     al abrir el optimizador.

export function AdminOptimizerConfigsPage() {
  const [profiles, setProfiles] = useState<OptimizerConfigProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<OptimizerConfigProfile | "new" | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await api.listOptimizerConfigs();
      setProfiles(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const onDelete = useCallback(
    async (p: OptimizerConfigProfile) => {
      if (!window.confirm(`¿Borrar el perfil "${p.name}"?`)) return;
      try {
        await api.deleteOptimizerConfig(p.id);
        await reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [reload],
  );

  return (
    <div className="space-y-4 p-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-ink">Configuraciones del optimizador</h1>
          <p className="text-sm text-sub">
            Perfiles reutilizables de jornada, capacidades y proxies. El planner elige
            uno al iniciar la planificación.
          </p>
        </div>
        <Button variant="primary" onClick={() => setEditing("new")}>
          Nuevo perfil
        </Button>
      </header>

      {error && (
        <div className="text-sm text-state-failed bg-state-failed/10 border border-state-failed/30 rounded p-2">
          {error}
        </div>
      )}

      <Card padding="none">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-sub border-b border-border">
              <tr>
                <th className="px-3 py-2">Nombre</th>
                <th className="px-3 py-2">Descripción</th>
                <th className="px-3 py-2 text-right">Jornada</th>
                <th className="px-3 py-2 text-right">% Peso</th>
                <th className="px-3 py-2 text-right">% Vol</th>
                <th className="px-3 py-2 text-right">Estabilidad</th>
                <th className="px-3 py-2 text-center">Default</th>
                <th className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {!loading && profiles.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-6 text-center text-sub">
                    No hay perfiles. Crea uno con "Nuevo perfil".
                  </td>
                </tr>
              )}
              {profiles.map((p) => (
                <tr key={p.id} className="border-t border-border hover:bg-page/60">
                  <td className="px-3 py-2 font-medium text-ink">{p.name}</td>
                  <td className="px-3 py-2 text-sub text-xs">{p.description || "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{p.settings.maxHours}h</td>
                  <td className="px-3 py-2 text-right tabular-nums">{p.settings.maxWeightPct}%</td>
                  <td className="px-3 py-2 text-right tabular-nums">{p.settings.maxVolumePct}%</td>
                  <td className="px-3 py-2 text-right tabular-nums">{p.settings.stabilityPenalty}</td>
                  <td className="px-3 py-2 text-center">
                    {p.is_default && <Badge variant="delivered">Default</Badge>}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button
                      onClick={() => setEditing(p)}
                      className="text-primary hover:text-primary-hover text-xs mr-3"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => onDelete(p)}
                      disabled={profiles.length <= 1}
                      title={profiles.length <= 1 ? "No se puede borrar el último perfil" : ""}
                      className="text-state-failed hover:text-state-failed/80 text-xs disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      Borrar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {editing && (
        <OptimizerConfigEditor
          profile={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void reload();
          }}
        />
      )}
    </div>
  );
}

// ---------------- Editor (modal) ----------------

function OptimizerConfigEditor({
  profile,
  onClose,
  onSaved,
}: {
  profile: OptimizerConfigProfile | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = profile === null;
  const [name, setName] = useState(profile?.name ?? "");
  const [description, setDescription] = useState(profile?.description ?? "");
  const [isDefault, setIsDefault] = useState(profile?.is_default ?? false);
  const [settings, setSettings] = useState<OptimizerConfig>(
    profile?.settings ?? DEFAULT_OPTIMIZER_CONFIG,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const patch = (next: Partial<OptimizerConfig>) =>
    setSettings((prev) => ({ ...prev, ...next }));

  const onSubmit = async () => {
    if (!name.trim()) {
      setError("Nombre requerido");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (isNew) {
        await api.createOptimizerConfig({
          name: name.trim(),
          description: description.trim(),
          settings,
          is_default: isDefault,
        });
      } else {
        await api.updateOptimizerConfig(profile!.id, {
          name: name.trim(),
          description: description.trim(),
          settings,
          is_default: isDefault,
        });
      }
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-card rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <h2 className="text-lg font-semibold text-ink">
            {isNew ? "Nuevo perfil del optimizador" : `Editar: ${profile!.name}`}
          </h2>
          <button
            className="text-sub hover:text-ink text-xl leading-none"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Identificación */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Nombre">
              <input
                type="text"
                className="w-full border border-border rounded px-2 py-1.5 text-sm"
                placeholder="ej. Viernes alto volumen"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
            <Field label="Descripción (opcional)">
              <input
                type="text"
                className="w-full border border-border rounded px-2 py-1.5 text-sm"
                placeholder="Cuándo usar este perfil"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Field>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
            />
            Marcar como default (auto-seleccionado al abrir el optimizador)
          </label>

          {/* Restricciones duras */}
          <Section title="Restricciones duras">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Field label="Jornada máx (horas)">
                <NumberInput value={settings.maxHours} onChange={(v) => patch({ maxHours: v })} />
              </Field>
              <Field label="Servicio por cliente (min)">
                <NumberInput
                  value={settings.serviceMinutes}
                  onChange={(v) => patch({ serviceMinutes: v })}
                />
              </Field>
              <Field label="Estabilidad (penalty)">
                <NumberInput
                  value={settings.stabilityPenalty}
                  onChange={(v) => patch({ stabilityPenalty: v })}
                />
              </Field>
            </div>
          </Section>

          {/* Capacidades */}
          <Section title="Capacidad de vehículo">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Field label="% peso máximo permitido">
                <NumberInput
                  value={settings.maxWeightPct}
                  onChange={(v) => patch({ maxWeightPct: v })}
                />
              </Field>
              <Field label="% volumen máximo permitido">
                <NumberInput
                  value={settings.maxVolumePct}
                  onChange={(v) => patch({ maxVolumePct: v })}
                />
              </Field>
              <Toggle
                label="Usar peso"
                value={settings.useWeight}
                onChange={(v) => patch({ useWeight: v })}
              />
              <Toggle
                label="Usar volumen"
                value={settings.useVolume}
                onChange={(v) => patch({ useVolume: v })}
              />
            </div>
          </Section>

          {/* Proxies */}
          <Section title="Proxies para CSV sin peso/volumen">
            <div className="grid grid-cols-2 gap-3">
              <Field label="kg por S/ (proxy peso)">
                <NumberInput
                  step={0.01}
                  value={settings.kgPerSol}
                  onChange={(v) => patch({ kgPerSol: v })}
                />
              </Field>
              <Field label="m³ por S/ (proxy volumen)">
                <NumberInput
                  step={0.0001}
                  value={settings.m3PerSol}
                  onChange={(v) => patch({ m3PerSol: v })}
                />
              </Field>
            </div>
          </Section>

          {/* Objetivos */}
          <Section title="Objetivos opcionales">
            <Toggle
              label="Balancear ventas (S/) entre choferes"
              value={settings.useSales}
              onChange={(v) => patch({ useSales: v })}
            />
          </Section>

          {error && (
            <div className="text-sm text-state-failed bg-state-failed/10 border border-state-failed/30 rounded p-2">
              {error}
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-border flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={onSubmit} disabled={saving}>
            {saving ? "Guardando…" : isNew ? "Crear perfil" : "Guardar cambios"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="block text-sub text-xs mb-1">{label}</span>
      {children}
    </label>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border-t border-border pt-3 first:border-t-0 first:pt-0">
      <div className="text-xs font-semibold uppercase tracking-wide text-sub mb-2">{title}</div>
      {children}
    </div>
  );
}

function NumberInput({
  value,
  onChange,
  step,
}: {
  value: number;
  onChange: (v: number) => void;
  step?: number;
}) {
  return (
    <input
      type="number"
      step={step ?? 1}
      value={Number.isFinite(value) ? value : 0}
      onChange={(e) => {
        const n = parseFloat(e.target.value);
        onChange(Number.isFinite(n) ? n : 0);
      }}
      className="w-full border border-border rounded px-2 py-1.5 text-sm tabular-nums"
    />
  );
}

function Toggle({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label}
    </label>
  );
}

// Re-export para que ProgramacionPlanPage (M11.5) pueda usar el mismo
// editor inline si lo necesita. Por ahora solo lo consume esta página.
export { OptimizerConfigEditor };

// Soporte legacy: importar el tipo aquí para no redefinirlo.
// Mantener este re-export evita un import cíclico cuando el page
// principal arme la lista del dropdown.
export type { OptimizerConfigProfile } from "../data/api";
